import { normalizeScope, type ScopeInput } from '@rentguru/commons-utils';
import { useQueryClient } from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';

import { useFilterGroups } from 'src/hooks/FilterGroupsContext';
import { useUser } from 'src/hooks/UserContext';
import { usePermissions } from 'src/hooks/utils/PermissionsContext';

import { type RentLiveViewHandler } from './rentLiveUpdateUtils';
import {
  createInvalidationCoalescer,
  RENT_INVALIDATION_MAX_WAIT_IN_MS,
  RENT_INVALIDATION_TRAILING_IN_MS,
  type InvalidationCoalescer,
} from './rentMatrixWindowUtils';
import RentTrackingInvalidationListener from './RentTrackingInvalidationListener';
import { invalidateRentTracking, type RentTrackingScope } from './rentTrackingQueries';
import { buildScopeInput, canViewRentTracking } from './rentTrackingUtils';

interface RentTrackingContextValue extends RentTrackingScope {
  registerLiveView: (handler: RentLiveViewHandler) => () => void;
}

const RentTrackingContext = createContext<RentTrackingContextValue | null>(null);

interface RentTrackingProviderProps {
  children: ReactNode;
}

export const RentTrackingProvider = ({ children }: RentTrackingProviderProps) => {
  const queryClient = useQueryClient();
  const { clientId, isOwner } = useUser();
  const permissions = usePermissions();
  const { selectedManagementFilterGroups } = useFilterGroups();
  const liveViewsRef = useRef<Set<RentLiveViewHandler>>(new Set());

  const scope = useMemo<ScopeInput>(
    () => buildScopeInput(selectedManagementFilterGroups),
    [selectedManagementFilterGroups]
  );
  const scopeHash = useMemo<string>(() => JSON.stringify(normalizeScope(scope)), [scope]);
  const coalescer = useMemo<InvalidationCoalescer>(
    () =>
      createInvalidationCoalescer({
        maxWaitInMs: RENT_INVALIDATION_MAX_WAIT_IN_MS,
        onFlush: (pending) => {
          if (isEmpty(liveViewsRef.current)) {
            invalidateRentTracking(queryClient, clientId);
            return;
          }
          liveViewsRef.current.forEach((handler) => handler(pending));
        },
        trailingInMs: RENT_INVALIDATION_TRAILING_IN_MS,
      }),
    [clientId, queryClient]
  );

  useEffect(() => () => coalescer.cancel(), [coalescer]);

  const registerLiveView = useCallback<RentTrackingContextValue['registerLiveView']>((handler) => {
    liveViewsRef.current.add(handler);
    return () => {
      liveViewsRef.current.delete(handler);
    };
  }, []);

  const value = useMemo<RentTrackingContextValue>(
    () => ({ registerLiveView, scope, scopeHash }),
    [registerLiveView, scope, scopeHash]
  );

  return (
    <RentTrackingContext.Provider value={value}>
      {canViewRentTracking(permissions, isOwner) && <RentTrackingInvalidationListener coalescer={coalescer} />}
      {children}
    </RentTrackingContext.Provider>
  );
};

export const useRentTracking = (): RentTrackingContextValue => {
  const context = useContext(RentTrackingContext);
  if (isNil(context)) {
    throw new Error('useRentTracking must be used within a RentTrackingProvider');
  }
  return context;
};
